import { useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { logger } from "@/utils/logger";

export function LoginForm({
  className,
  apiBaseUrl,
  ...props
}: React.ComponentProps<"div"> & { apiBaseUrl?: string }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    if (!apiBaseUrl) {
      setError("API URL configuration error. Please contact administrator.");
      setIsLoading(false);
      logger.error("Login failed due to missing API URL configuration");
      return;
    }

    try {
      // Use the standard OAuth2 token endpoint relative to the API base URL
      const url = `${apiBaseUrl}/token`;

      // Create form data for OAuth2 password flow
      const formData = new FormData();
      formData.append("username", username);
      formData.append("password", password);
      formData.append("grant_type", "password");

      // Send form data - secure when using HTTPS
      const response = await fetch(url, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        let errorMessage = "Login failed. Please try again.";

        try {
          const errorText = await response.text();

          // Try to parse as JSON if possible
          try {
            const errorData = JSON.parse(errorText);
            errorMessage = errorData.detail || errorMessage;
          } catch {
            // Not JSON, use text directly if it's meaningful
            if (errorText && errorText.length < 100) {
              errorMessage = errorText;
            }
          }
        } catch {
          logger.error("Error reading login response", {
            status: response.status,
          });
        }

        if (response.status === 401) {
          errorMessage = "Invalid username or password.";
          logger.warn("Failed login attempt", { username }); // Log username for security monitoring
        } else if (response.status === 403) {
          errorMessage = "Access forbidden. Contact administrator.";
          logger.error("Forbidden access attempt", {
            username,
            status: response.status,
          });
        } else if (response.status === 0 || !response.status) {
          errorMessage = "Network error. CORS issue or server unavailable.";
          logger.error("Network error during login", {
            status: response.status,
          });
        }

        setError(errorMessage);
        setIsLoading(false);
        return;
      }

      // Handle successful response
      const data = await response.json();
      logger.info("User logged in successfully", { username });
      login(data.access_token);
    } catch (err) {
      logger.error("Login error", { error: (err as Error).message });
      setError("Network error. Please check if the backend server is running.");
      setIsLoading(false);
    }
  };

  return (
    <div className={`flex flex-col items-center ${className || ''}`} {...props}>
      <div className="card w-full max-w-sm overflow-hidden shadow-none backdrop-blur-sm bg-neutral/90 border-none mb-4">
        <div className="card-body p-6 md:p-8">
          <form onSubmit={handleSubmit}>
            <div className="flex flex-col gap-6">
              <div className="flex flex-col items-center text-center">
                <h1 className="text-xl card-title justify-center text-neutral-content">AI Content Manager</h1>
              </div>

              {error && (
                <p className="text-error text-sm text-center mb-4">{error}</p>
              )}

              <div className="form-control w-full">
                <label className="label" htmlFor="username">
                  <span className="label-text text-neutral-content mb-2">Username</span>
                </label>
                <input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  className="input input-bordered w-full focus:outline-none"
                />
              </div>

              <div className="form-control w-full">
                <label className="label" htmlFor="password">
                   <span className="label-text text-neutral-content mb-2">Password</span>
                </label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="input input-bordered w-full focus:outline-none"
                />
              </div>

              <button
                type="submit"
                className="btn bg-primary text-primary-content border-none w-full"
                disabled={isLoading}
              >
                {isLoading && <span className="loading loading-spinner"></span>}
                {isLoading ? "Logging in..." : "Login"}
              </button>

            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
